/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from 'axios';
import { API_CONFIG, buildStrapiQuery, FILTER_OPERATORS } from '@/lib/config';
import { Service, Faq, AboutUsData } from '@/lib/types';
import i18next from 'i18next';

// Type for API response
type StrapiResponse<T> = {
  data: T;
  meta: {
    pagination?: {
      page: number;
      pageSize: number;
      pageCount: number;
      total: number;
    };
  };
};

// Generic fetch function
export const fetchAPI = async <T>(endpoint: string, queryParams = ''): Promise<StrapiResponse<T>> => {
  const url = `${API_CONFIG.STRAPI_BASE_URL}${endpoint}${queryParams ? `?${queryParams}` : ''}`;
  const response = await axios.get(url);
  return response.data;
};

export const servicesApi = {
  // Get all services
  getAll: (params = {}) => fetchAPI<Service[]>(API_CONFIG.SERVICES_ENDPOINT, buildStrapiQuery(params)),

  // Get service by ID
  getById: (id: string | number, populate: any = '*') =>
    fetchAPI<Service>(`${API_CONFIG.SERVICES_ENDPOINT}/${id}`, buildStrapiQuery({ populate })),

  // Search services
  search: (term: string) => {
    const params = new URLSearchParams();
    params.append('query', term);
    params.append('locale', i18next.language);
    return fetchAPI<Service[]>(API_CONFIG.SERVICES_ENDPOINT, params.toString());
  },

  // Get services by category
  getByCategory: (categoryId: number | string) =>
    fetchAPI<Service[]>(
      API_CONFIG.SERVICES_ENDPOINT,
      buildStrapiQuery({
        filters: {
          service_category: { id: { [FILTER_OPERATORS.EQUALS]: categoryId } },
        },
        populate: '*',
      }),
    ),

  // Get services by authority
  getByAuthority: (authorityId: number | string) =>
    fetchAPI<Service[]>(
      API_CONFIG.SERVICES_ENDPOINT,
      buildStrapiQuery({
        filters: {
          authority: { id: { [FILTER_OPERATORS.EQUALS]: authorityId } },
        },
        populate: '*',
      }),
    ),

  // Get services by zone
  getByZone: (zoneId: number | string) =>
    fetchAPI<Service[]>(
      API_CONFIG.SERVICES_ENDPOINT,
      buildStrapiQuery({
        filters: {
          zones: { id: { [FILTER_OPERATORS.EQUALS]: zoneId } },
        },
        populate: '*',
      }),
    ),
};

export const faqsApi = {
  getAll: (params = {}) => fetchAPI<Faq[]>(API_CONFIG.FAQS_ENDPOINT, buildStrapiQuery(params)),

  getById: (id: string | number, populate: any = '*') =>
    fetchAPI<Faq>(`${API_CONFIG.FAQS_ENDPOINT}/${id}`, buildStrapiQuery({ populate })),
};

export const aboutUsApi = {
  getAll: (
    params = {
      populate: ['*'],
    },
  ) => fetchAPI<AboutUsData>(API_CONFIG.ABOUT_US_ENDPOINT, buildStrapiQuery(params)),

  getById: (id: string | number, populate: any = '*') =>
    fetchAPI<AboutUsData>(`${API_CONFIG.ABOUT_US_ENDPOINT}/${id}`, buildStrapiQuery({ populate })),
};

export const searchApi = {
  search: (term: string) => {
    const params = new URLSearchParams();
    params.append('query', term);
    params.append('locale', i18next.language);
    return fetchAPI<Service[]>(API_CONFIG.SEARCH_ENDPOINT, params.toString());
  },
};
